import { fabric } from 'fabric'
import { v4 as guid } from 'uuid'
import { clone, debounce, pick } from 'lodash-es'
import Utility from '../services/utility'
import WbDesignOption from './designOption'
import type DuneAsset from '@/models/duneAsset'
import appConfig from '@/services/appConfig'
import pngLoading from '@/assets/loading.png'
import pngNoImage from '@/assets/noimg.png'
import { appConstants, privileges, whiteboardConstants } from '@/models/constants'
import { useUserStore } from '@/store/userData'
import { getArticleAssets } from '@/api/t1/article'
import type MyArticle from '@/models/myArticle'
import utils from '@/services/utils'
import { getDesignOptionAssets } from '@/api/dune/asset'
import promiseQueue from '@/services/promiseQueue'

interface IWbModelImageOptions extends fabric.IImageOptions {
  id?: string
  catalogCode: number
  modelId: number
  articleId: number
  article?: MyArticle
  lock?: boolean
  preventUnlock?: boolean
  preventDelete?: boolean
  excludeFromGroupSelection?: boolean
  localOnly?: boolean
  assetKey?: string
  showDesignOptions?: boolean
  designOptionData?: IDesignOptionPosition[]
  modelNumber?: string
  icons?: string[]
}

export default class WbModelImage extends fabric.Image implements IWbObject {
  public id: string
  public catalogCode: number
  public modelId: number
  public articleId: number
  public article?: MyArticle
  public assets: DuneAsset[]
  public designOptions: DuneAsset[]
  public assetKey?: string
  public type = whiteboardConstants.objectTypes.modelImage
  public selected = false
  public highlighted = false
  public _isMoving = false
  public lock: boolean
  public preventUnlock: boolean
  public connectable: boolean
  public imgLoaded: boolean
  public imgLoading: boolean
  public localOnly?: boolean
  public preventDelete?: boolean
  public excludeFromGroupSelection?: boolean
  public showDesignOptions: boolean
  public designOptionData: IDesignOptionPosition[] = []
  private designOptionsObjects: fabric.Object[] = []
  public icons: string[] = []

  public canBeLinkedTo = true
  public canLink = false
  public link = undefined
  public modelNumber?: string
  public editableProps: Record<string, IWbObjectProp> = {
    imageType: { name: 'Image Type', type: 'imageType', editable: true },
    scale: { name: 'Size', type: 'scale', editable: true },
    lock: { name: 'Lock', type: 'lock', editable: true, editableWhenLocked: true },
    addModelDetails: { name: 'Add Model Details', type: 'addModelDetails', editable: true },
    addDiscussion: { name: 'Add Comment', type: 'addDiscussion', editable: true, editableWhenLocked: true },
    showDesignOptions: { name: 'Show Design Options', type: 'showDesignOptions', editable: true, editableWhenLocked: true },
    icons: { name: 'Add Icons', type: 'icons', editable: true, editableWhenLocked: true },
    info: { name: 'Info', type: 'info', editable: true, editableWhenLocked: true },
  }

  public actions: Record<string, IWObjectActions> = {
    selectSimilar: { action: 'selectSimilar', label: 'Select Similar', faicon: 'fa-light fa-check-double', showInSubMenu: true },
    bringFront: { action: 'bringFront', label: 'Bring to Front', faicon: 'fa-light fa-bring-front', showInSubMenu: true },
    sendBack: { action: 'sendBack', label: 'Send to Back', faicon: 'fa-light fa-send-back', showInSubMenu: true },
    // group: { action: 'group', label: 'Group', faicon: 'fa-light fa-object-group', showInSubMenu: true, multiple: true },
    copyModel: {
      action: 'copyModel',
      label: 'Copy Model',
      faicon: 'fa-light fa-copy',
      showInSubMenu: (() => {
        const userStore = useUserStore()
        return userStore.activeCatalog?.DataSourceTypeId !== appConstants.catalogTypes.inherited
          && !userStore.currentCustomer
          && userStore.userProfile.isValidPrivilege(privileges.article.carryOverArticles)
          && userStore.userProfile.isValidPrivilege(privileges.article.createModelWithArticles)
          && (userStore.userProfile.AccountDetails.AccountTypeId === 1 || userStore.userProfile.AccountDetails.AccountId === userStore.activeCatalog!.AccountId)
      })(),
    },
    cloneAsImage: { action: 'cloneAsImage', label: 'Copy as Image', faicon: 'fa-light fa-clone', showInSubMenu: true },
    delete: { action: 'delete', label: 'Remove', faicon: 'fa-light fa-trash-can', showInSubMenu: true },
  }

  constructor(element: string | HTMLImageElement | HTMLCanvasElement | HTMLVideoElement, options: IWbModelImageOptions) {
    super(element, options)
    this.id = options.id || guid()
    const userStore = useUserStore()

    this.catalogCode = options.catalogCode
    this.modelId = options.modelId
    this.articleId = options.articleId
    this.article = options.article
    this.assets = []
    this.designOptions = []
    this.lock = options.lock || false
    this.assetKey = options.assetKey
    this.connectable = true
    this.imgLoaded = false
    this.imgLoading = false
    this.localOnly = options.localOnly || false
    this.preventUnlock = options.preventUnlock || false
    this.preventDelete = options.preventDelete || false
    this.excludeFromGroupSelection = options.excludeFromGroupSelection || false
    this.showDesignOptions = utils.isDefined(options?.showDesignOptions) ? Boolean(options?.showDesignOptions) : userStore.activeCatalog && userStore.userPreferences && userStore.userPreferences.whiteboard && userStore.userPreferences.whiteboard.settings && userStore.userPreferences.whiteboard.settings[userStore.activeCatalog.CatalogCode] && userStore.userPreferences.whiteboard.settings[userStore.activeCatalog.CatalogCode].showModelDesignOptions ? userStore.userPreferences.whiteboard.settings[userStore.activeCatalog.CatalogCode].showModelDesignOptions : false
    this.designOptionsObjects = []
    this.designOptionData = options?.designOptionData || []
    this.modelNumber = options.modelNumber || ''
    this.icons = options.icons || []
    // if (isLiveObject) {
    //   const userStore = useUserStore()
    //   const observable = liveQuery(async () => appConfig.DB!.getArticlesByCatalogCodeModelNumbers(userStore.activeCatalog!, [this.modelNumber], userStore.currentCustomer, userStore.currentCustomerSegmentations).then(collection => collection.toArray()))
    //   const subscription = observable.subscribe((modelArticles) => {
    //     // As discussed with Andre we dont need to set article again when we first time create the object as already we are loading the article
    //     if (!this.isLoadingFirstTime) {
    //       if (modelArticles && modelArticles.length && modelArticles.some(article => article.Status === 1)) {
    //         if (this.opacity !== 1) {
    //           this.setProp('opacity', { opacity: 1 }, true)
    //         }
    //       }
    //       else if (this.opacity !== 0.5) {
    //         this.setProp('opacity', { opacity: 0.5 }, true)
    //       }
    //     }
    //     else {
    //       this.isLoadingFirstTime = false
    //     }
    //   })
    //   this.subscriptions.push(subscription)
    // }

    this.setLock(this.lock)

    this.stateProperties = this.stateProperties?.concat(['lock'])

    this.on('added', () => {
      this.handleDesignOptionsVisibility(this.showDesignOptions)
    })

    this.on('selected', () => {
      this.selected = true
    })

    this.on('deselected', () => {
      this.selected = false
    })

    this.on('removed', () => {
      if (this.designOptionsObjects.length > 0) {
        this.canvas?.remove(...this.designOptionsObjects)
      }
    })

    this.on('moving', () => {
      this._isMoving = true
    })

    this.on('mouseup', () => {
      this._isMoving = false
    })

    this.on('scaling', () => {
      this.updateLinkWhenUpdated()
      this.canvas?.fire('object:modified', { target: this })

      this.canvas?.requestRenderAll()
    })
  }

  updateLinkWhenUpdated() {
    const articleCoords = fabric.util.qrDecompose(this.calcTransformMatrix())

    this.designOptionsObjects.forEach((designObject) => {
      const designOptionObject = designObject as WbDesignOption
      const assetId = designOptionObject.assetId
      if (assetId) {
        const pos = this.designOptionData.find(p => p.id === assetId)
        if (!pos) { return }
        const designObjectCoords = fabric.util.qrDecompose(designOptionObject.calcTransformMatrix())
        // Convert from center-origin to top-left
        const topLeftX = designObjectCoords.translateX - (designOptionObject.width! * designOptionObject.scaleX!) / 2
        const topLeftY = designObjectCoords.translateY - (designOptionObject.height! * designOptionObject.scaleY!) / 2

        pos.link = {
          x: (topLeftX - articleCoords.translateX) / this.scaleX!,
          y: (topLeftY - articleCoords.translateY) / this.scaleY!,
        }
      }
    })
  }

  onServerUpdate() {
    if (this._isMoving) {
      this.updateLinkWhenUpdated()
    }
    this.canvas?.requestRenderAll()
  }

  private async createDesignOptionObjects(designOptions: DuneAsset[]) {
    this.canvas?.remove(...this.designOptionsObjects)
    this.designOptionsObjects = []

    const horizontalGap = 20 // first time
    const verticalStartGap = 50 // first time
    const gapBetweenDesignOptions = 10 // first time

    const parentMatrix = this.calcTransformMatrix()
    const parentCoords = fabric.util.qrDecompose(parentMatrix)
    const invParentMatrix = fabric.util.invertTransform(parentMatrix)

    const scaledW = (this.width || 0) * (this.scaleX || 1)
    const scaledH = (this.height || 0) * (this.scaleY || 1)

    const baseLeft = parentCoords.translateX + scaledW / 2 + horizontalGap
    let currentTop = parentCoords.translateY - scaledH / 2 + verticalStartGap
    const existingDesignOptions: DuneAsset[] = []
    const nonExistingDesignOptions: DuneAsset[] = []
    for (const designOption of designOptions) {
      const position = this.designOptionData.find(p => p.id === designOption.Id)
      if (position) {
        existingDesignOptions.push(designOption)
      }
      else {
        nonExistingDesignOptions.push(designOption)
      }
    }
    let totalExistingHeight = 0

    // --- existing options ---
    for (const designOption of existingDesignOptions) {
      const position = this.designOptionData.find(p => p.id === designOption.Id)!
      const currentHeightAndWidth = WbDesignOption.getDesignOptionHeightWidth(designOption.Width, designOption.Height, position.scale)

      const maxActualSize = Math.max(designOption.Width, designOption.Height)
      const aspectRatio = designOption.Width / designOption.Height
      const currentMaxSize = Math.max(currentHeightAndWidth.scaledWidth, currentHeightAndWidth.scaledHeight)
      const newResolution = WbDesignOption.getNearestResolution(currentMaxSize, maxActualSize)

      const width = designOption.Height > designOption.Width
        ? Math.round(newResolution / (currentHeightAndWidth.scaledHeight / currentHeightAndWidth.scaledWidth))
        : newResolution
      const height = designOption.Height > designOption.Width
        ? newResolution
        : Math.round(newResolution / aspectRatio)

      const scaleX = currentHeightAndWidth.scaledWidth / width
      const scaleY = currentHeightAndWidth.scaledHeight / height

      //  compute world position from stored local link
      const worldPt = fabric.util.transformPoint(
        new fabric.Point(position.link.x, position.link.y),
        parentMatrix,
      )

      const options = { article: this.article, assetId: designOption.Id, asset: designOption, articleId: this.articleId, assetStorageFile: designOption.StorageFile, top: worldPt.y, left: worldPt.x, scaleX, scaleY }

      const designOptionObject = await WbDesignOption.loadFromUrl(options, width, height) as WbDesignOption
      totalExistingHeight += currentHeightAndWidth.scaledHeight + gapBetweenDesignOptions
      this.setDesignOptionEventHandlers(designOptionObject, designOption.Id)
      //  recompute link back into local coords
      const childCoords = fabric.util.qrDecompose(designOptionObject.calcTransformMatrix())
      const childWorld = new fabric.Point(childCoords.translateX, childCoords.translateY)
      const childLocal = fabric.util.transformPoint(childWorld, invParentMatrix)
      designOptionObject.link = { id: this.id, x: childLocal.x, y: childLocal.y } as any
      this.designOptionsObjects.push(designOptionObject)
      this.canvas?.add(designOptionObject)
    }

    currentTop += totalExistingHeight
    // --- new options ---
    for (const designOption of nonExistingDesignOptions) {
    //  convert new world coords → local
      const localPt = fabric.util.transformPoint(
        new fabric.Point(baseLeft, currentTop),
        invParentMatrix,
      )

      const position = {
        id: designOption.Id,
        link: { x: localPt.x, y: localPt.y },
        scale: WbDesignOption.getDesignOptionScale(designOption.Width, designOption.Height),
      }
      this.designOptionData.push(position)

      // convert back local → world for placement
      const worldPt = fabric.util.transformPoint(
        new fabric.Point(localPt.x, localPt.y),
        parentMatrix,
      )

      const currentData = WbDesignOption.getDesignOptionHeightWidth(designOption.Width, designOption.Height, position.scale)
      const options = { article: this.article, assetId: designOption.Id, asset: designOption, articleId: this.articleId, assetStorageFile: designOption.StorageFile, top: worldPt.y, left: worldPt.x }
      const designOptionObject = await WbDesignOption.loadFromUrl(options, currentData.scaledWidth, currentData.scaledHeight) as WbDesignOption
      this.setDesignOptionEventHandlers(designOptionObject, designOption.Id)

      // store link back in local coords
      const childCoords = fabric.util.qrDecompose(designOptionObject.calcTransformMatrix())
      const childWorld = new fabric.Point(childCoords.translateX, childCoords.translateY)
      const childLocal = fabric.util.transformPoint(childWorld, invParentMatrix)

      designOptionObject.link = { id: this.id, x: childLocal.x, y: childLocal.y } as any

      this.designOptionsObjects.push(designOptionObject)
      this.canvas?.add(designOptionObject)
      currentTop += designOptionObject.height! + gapBetweenDesignOptions
    }
    if (nonExistingDesignOptions.length > 0) {
      this.canvas?.fire('object:modified', { target: this })
    }
    this.canvas?.requestRenderAll()
  }

  private handleDesignOptionsVisibility(visible: boolean) {
    const userStore = useUserStore()
    if (visible) {
      const catalogDetails = userStore.activeCatalog
      if (catalogDetails && this.article) {
        getDesignOptionAssets(catalogDetails.DuneContext, catalogDetails.ContextKey, this.article.ModelNumber, false, whiteboardConstants.abortController.signal)
          .then((assets) => {
            this.designOptions = assets
            if (this.designOptions.length > 0) {
              this.createDesignOptionObjects(this.designOptions)
              // remove deleted design options data
              const validIds = new Set(this.designOptions.map(r => r.Id))
              this.designOptionData = this.designOptionData.filter(r => validIds.has(r.id))
            }
            else if (this.showDesignOptions) { // if no design options then set the show design options false
              this.designOptionData = []
              // this.setProp('showDesignOptions', { showDesignOptions: false }, true)
            }
          }).catch((_e) => {
            if (this.designOptions.length > 0) {
              this.createDesignOptionObjects(this.designOptions)
              // remove deleted design options data
              const validIds = new Set(this.designOptions.map(r => r.Id))
              this.designOptionData = this.designOptionData.filter(r => validIds.has(r.id))
            }
            else if (this.showDesignOptions) { // if no design options then set the show design options false
              this.designOptionData = []
              // this.setProp('showDesignOptions', { showDesignOptions: false }, true)
            }
          })
      }
    }
    else {
      this.designOptionsObjects.forEach((obj) => {
        if (this.canvas?.contains(obj)) {
          this.canvas?.remove(obj)
        }
      })
      this.designOptionsObjects = []
      this.canvas?.requestRenderAll()
    }
  }

  private setDesignOptionEventHandlers(designOptionObject: fabric.Object, assetId: string) {
    const designObject = designOptionObject as WbDesignOption
    designOptionObject.on('modified', () => {
      const articleCoords = fabric.util.qrDecompose(this.calcTransformMatrix())
      const pos = this.designOptionData.find(p => p.id === assetId)
      if (!pos) { return }

      // pos.scale = pos.scale || 1

      const resourceCoords = fabric.util.qrDecompose(designOptionObject.calcTransformMatrix())

      pos.link = {
        // id: this.id,
        x: (resourceCoords.translateX - articleCoords.translateX) / this.scaleX!,
        y: (resourceCoords.translateY - articleCoords.translateY) / this.scaleY!,
      }
      this.canvas?.fire('object:modified', { target: this })
    })
    designObject.on('scaling', () => {
      const debouncedCheckAndReplace = debounce(() => {
        if (designObject.isUpdatingImage) { return }
        this.applyScaling(designOptionObject)
      }, 2000)
      if (!designObject.isUpdatingImage) {
        debouncedCheckAndReplace()
      }
    })
  }

  applyScaling(designOptionObject) {
    const userStore = useUserStore()
    const pos = this.designOptionData.find(p => p.id === designOptionObject.asset.Id)
    if (!pos) { return }
    const currentWidth = designOptionObject.width * designOptionObject.scaleX
    const currentHeight = designOptionObject.height * designOptionObject.scaleY
    const currentMaxSize = Math.max(currentWidth, currentHeight)
    const maxActualSize = Math.max(designOptionObject.asset.Width, designOptionObject.asset.Height)
    const aspectRatio = designOptionObject.asset.Width / designOptionObject.asset.Height

    const newResolution = WbDesignOption.getNearestResolution(currentMaxSize, maxActualSize)
    if (newResolution > Math.max(designOptionObject.width, designOptionObject.height)) {
      let newImageWidth = currentWidth
      let newImageHeight = Math.round(newResolution / aspectRatio)
      if (currentHeight > currentWidth) {
        newImageHeight = Math.round(currentHeight)
        newImageWidth = Math.round(newResolution / (currentHeight / currentWidth))
      }
      designOptionObject.isUpdatingImage = true
      const width = designOptionObject.asset.Height > designOptionObject.asset.Width ? newImageWidth : newResolution
      const height = designOptionObject.asset.Height > designOptionObject.asset.Width ? newResolution : newImageHeight
      const url = `${appConfig.AssetsUrl}/assets/content/${designOptionObject.assetStorageFile}?ContextKey=${encodeURIComponent(userStore.activeCatalog!.ContextKey)}&f=webp&w=${width}&h=${height}&trim=true`
      fabric.util.loadImage(url, (newImgEl) => {
        designOptionObject.setElement(newImgEl)
        designOptionObject.set({
          width,
          height,
          scaleX: width / currentWidth,
          scaleY: height / currentHeight,
        } as unknown as Partial<this>)
        pos.scale = (currentMaxSize / maxActualSize) || pos.scale
        designOptionObject.isUpdatingImage = false
        designOptionObject.canvas?.requestRenderAll()
        designOptionObject.canvas?.fire('object:modified', { target: this })
      }, null, 'anonymous')
    }
  }

  setProp(prop: string, value: any, ignoreHistory: boolean = false) {
    switch (prop) {
      case 'imageType':
        this.set('assetKey', value.assetKey)
        this.imgLoaded = false
        this.imgLoading = false
        break
      case 'scale':
        if (value.scale <= 100 && this.width && this.height) {
          const scale = value.scale / 100
          this.set('scaleX', scale)
          this.set('scaleY', scale)
        }
        break
      case 'lock':
        this.setLock(value.lock)
        break
      case 'opacity':
        this.set('opacity', value.opacity)
        break
      case 'showDesignOptions':
        this.set('showDesignOptions', value.showDesignOptions)
        this.handleDesignOptionsVisibility(Boolean(value.showDesignOptions))
        break
      case 'icons':
        this.set('icons', value.icons)
        break
      default:
        console.warn('attempting to set unsupported WbObjectProp', prop, value)
        return
    }
    this.dirty = true
    this.canvas?.requestRenderAll()
    this.canvas?.fire('object:modified', { target: this, ignoreHistory })
  }

  getProp(prop: string) {
    const result: any = {}
    switch (prop) {
      case 'imageType':
        result.assetKey = this.assetKey
        break
      case 'scale':
        result.scale = Math.round((this.scaleX || 1) * 100)
        break
      case 'lock':
        result.lock = this.lock
        break
      case 'showDesignOptions':
        result.showDesignOptions = this.showDesignOptions
        break
      case 'icons':
        result.icons = this.icons
        break
      default:
        console.warn('attempting to get unsupported WbObjectProp', prop)
    }
    return result
  }

  highlight() {
    this.highlighted = true
    this.set('stroke', '#2196f3')
    this.set('strokeWidth', 4)
    this.set('strokeDashArray', [20])
    this.canvas?.requestRenderAll()
  }

  dim() {
    this.highlighted = false
    this.set('stroke', undefined)
    this.set('strokeWidth', 0)
    this.set('strokeDashArray', undefined)
    this.canvas?.requestRenderAll()
  }

  midIntersectsWithObject(other: fabric.Object, absolute?: boolean, calculate?: boolean) {
    const points1 = this.getCoords(absolute, calculate)
    const points2 = other.getCoords(absolute, calculate)

    const xDelta = (points1[1].x - points1[0].x) / 4
    const yDelta = (points1[2].y - points1[0].y) / 4
    const increaseInX = points1[0].x + xDelta
    const increaseInY = points1[0].y + yDelta

    for (let i = 0; i < points1.length; i++) {
      const point = points1[i]
      point.x = (point.x >= increaseInX) ? (point.x - xDelta) : (point.x + xDelta)
      point.y = (point.y >= increaseInY) ? (point.y - yDelta) : (point.y + yDelta)
    }

    const intersection = fabric.Intersection.intersectPolygonPolygon(points1, points2)

    return intersection.status === 'Intersection'
      || other.isContainedWithinObject(this, absolute, calculate)
      || this.isContainedWithinObject(other, absolute, calculate)
  }

  setLock(lock: boolean) {
    this.set('lock', lock)
    this.set('lockMovementX', lock)
    this.set('lockMovementY', lock)
    this.set('lockRotation', lock)
    this.set('lockScalingFlip', lock)
    this.set('lockScalingX', lock)
    this.set('lockScalingY', lock)
    this.set('hasControls', !lock)
  }

  override toObject() {
    const propsToPluck = [
      'id',
      'type',
      'top',
      'left',
      'width',
      'height',
      'angle',
      'scaleX',
      'scaleY',
      'lock',
      'assetKey',
      'version',
      'preventUnlock',
      'backgroundColor',
      'catalogCode',
      'stroke',
      'strokeWidth',
      'strokeDashArray',
      'localOnly',
      'modelId',
      'articleId',
      'showDesignOptions',
      'designOptionData',
      'modelNumber',
      'icons',
    ]
    return pick(this, propsToPluck)
  }

  static fromObject(object: WbModelImage, callback?: Function) {
    this.loadModelImageByModelId(object.catalogCode, object.width || 500, object.height || 500, object).then((v) => { callback && callback(v) })
  }

  static async loadModelImageByModelId(catalogCode: number, width: number, height: number, options: IWbModelImageOptions) {
    if (options.article) {
      return await this.loadModelImage(options.article, width, height, options)
    }

    else if (options?.modelId) {
      const userStore = useUserStore()
      const catalogDetails = userStore.linkedCatalogDetails[catalogCode] || userStore.activeCatalog
      if (appConfig.DB && userStore.myAttributes) {
        const articles = await appConfig.DB.getFirstActiveMyArticleByModelIds([options?.modelId], catalogDetails, userStore.linkedCatalogDetails, userStore.myAttributes!, userStore.currentUsername, userStore.userProfile.Id, userStore.currentCustomer, userStore.priceGroups.retail, userStore.priceGroups.wholesale, userStore.priceGroups.outlet, [], true, whiteboardConstants.abortController.signal)
        if (articles && articles.length) {
          return await this.loadModelImage(articles[0], width, height, options)
        }
        else {
          console.warn('unable to find the article to refer', options)
        }
      }
    }
    else if (options?.modelNumber) {
      const userStore = useUserStore()
      const catalogDetails = userStore.linkedCatalogDetails[catalogCode] || userStore.activeCatalog
      if (appConfig.DB && userStore.myAttributes) {
        const articles = await appConfig.DB.getFirstActiveMyArticleByModelNumbers([options?.modelNumber], catalogDetails, userStore.linkedCatalogDetails, userStore.myAttributes!, userStore.currentUsername, userStore.userProfile.Id, userStore.currentCustomer, userStore.priceGroups.retail, userStore.priceGroups.wholesale, userStore.priceGroups.outlet, [], true, whiteboardConstants.abortController.signal)
        if (articles && articles.length) {
          return await this.loadModelImage(articles[0], width, height, options)
        }
        else {
          console.warn('unable to find the article to refer', options)
        }
      }
    }
    console.log('Failed to load model image by modelNumber', options)
    return null
  }

  static loadModelImage(article: MyArticle, width: number, height: number, options?: IWbModelImageOptions) {
    return new Promise<WbModelImage>((resolve) => {
      fabric.util.loadImage(pngLoading, async (img) => {
        const userStore = useUserStore()
        const currentOptions = Object.assign((options || { width, height }), { catalogCode: article.CatalogCode, articleId: article.Id, modelId: article.ModelId, article })
        const modelImageObject = new WbModelImage(img, currentOptions)
        modelImageObject.article = article
        modelImageObject.modelId = article.ModelId
        modelImageObject.articleId = article.Id
        modelImageObject.modelNumber = article.ModelNumber

        if (article._isAssetsLoaded) {
          modelImageObject.assets = article._Assets
          if (article._Assets.length && !modelImageObject.assetKey) {
            modelImageObject.assetKey = article._Assets[0].Id
            if (utils.isValidStringValue(userStore.activeCatalog?.Config.DefaultImageTypeForModel)) {
              const configuredKeyAssetAvailable = modelImageObject.assets.find(asset => asset.Key.toLowerCase() === userStore.activeCatalog!.Config.DefaultImageTypeForModel.toLowerCase())
              modelImageObject.assetKey = configuredKeyAssetAvailable ? configuredKeyAssetAvailable.Id : article._Assets[0].Id
            }
          }
        }
        else {
          const catalogDetails = userStore.linkedCatalogDetails[article.CatalogCode] || userStore.activeCatalog
          modelImageObject.imgLoading = true
          getArticleAssets(catalogDetails.DuneContext, catalogDetails.ContextKey, article.ModelNumber, userStore.activeCatalog?.Config.NewestImageAssetKeyList, false, whiteboardConstants.abortController.signal)
            .then((assets) => {
              modelImageObject.assets = assets
              if (!modelImageObject.assetKey && assets.length) {
                modelImageObject.assetKey = assets[0].Id
                if (utils.isValidStringValue(userStore.activeCatalog?.Config.DefaultImageTypeForModel)) {
                  const configuredKeyAssetAvailable = modelImageObject.assets.find(asset => asset.Key.toLowerCase() === userStore.activeCatalog!.Config.DefaultImageTypeForModel.toLowerCase())
                  modelImageObject.assetKey = configuredKeyAssetAvailable ? configuredKeyAssetAvailable.Id : assets[0].Id
                }
              }
              modelImageObject.canvas?.requestRenderAll()
            })
            .finally(() => {
              modelImageObject.imgLoaded = false
              modelImageObject.imgLoading = false
            })
        }

        resolve(modelImageObject)
      })
    })
  }

  static async getJsonObject(article: MyArticle, width: number, height: number, scaleValue, opt?: IWbModelImageOptions) {
    const userStore = useUserStore()
    const o = Object.assign((opt || { width, height }), { catalogCode: article.CatalogCode, modelId: article.ModelId, articleId: article.Id })
    const catalogDetails = userStore.linkedCatalogDetails[article.CatalogCode] || userStore.activeCatalog

    const obj: any = new WbModelImage('', o).toObject()
    if (scaleValue.scale <= 100 && width && height) {
      const scale = scaleValue.scale / 100
      obj.scaleX = scale
      obj.scaleY = scale
    }
    const modelAssets = await getArticleAssets(catalogDetails.DuneContext, catalogDetails.ContextKey, article.ModelNumber, userStore.activeCatalog?.Config.NewestImageAssetKeyList)
    if (opt && modelAssets && modelAssets.length > 0 && catalogDetails) {
      let asset = modelAssets[0]

      if (opt && opt.assetKey === 'firstImage') {
        const assetsClone = clone(modelAssets)
        utils.sort(assetsClone, ['SortOrder'])
        asset = assetsClone[0]
      }
      else if (opt.assetKey === 'newestImage') {
        asset = article._Assets[0]
      }
      else {
        // if requested for specific assetKey then search in _Assets, if does not exist in _Assets it should show placeholder
        if (utils.isValidStringValue(opt.assetKey)) {
          const selectedAsset = modelAssets.find(asset => asset.Id === opt.assetKey || asset.Key === opt.assetKey)
          if (selectedAsset) {
            asset = selectedAsset
          }
        }
      }
      obj.src = `${appConfig.AssetsUrl}/assets/content/${asset.StorageFile}?ContextKey=${encodeURIComponent(catalogDetails.ContextKey)}&f=webp&w=${width}&h=${height}&trim=true`
    }
    obj.height = height
    obj.width = width
    return obj
  }

  override _render(ctx: CanvasRenderingContext2D): void {
    super._render(ctx)
    if (!this.imgLoaded && !this.imgLoading) {
      if (this.assets && this.assets.length > 0) {
        this.imgLoading = true
        const src = WbModelImage.getImageSrc(this.catalogCode, this.width || 500, this.height || 500, this.assets, this.assetKey)
        promiseQueue.add(() => new Promise((resolve, reject) => this.setSrc(src, (img, error) => error ? reject(error) : resolve(img), { crossOrigin: 'anonymous' }))
          .then(() => {
            this.canvas?.requestRenderAll()
          }).catch((error) => {
            this.setSrc(pngNoImage, () => this.canvas?.requestRenderAll())
            console.warn('unable to load WbModelImage', this, error)
          }).finally(() => {
            this.imgLoaded = true
            this.imgLoading = false
          }),
        )
      }
      else {
        if (this.getSrc() !== pngNoImage) {
          this.setSrc(pngNoImage, () => this.canvas?.requestRenderAll())
        }
      }
    }
    if (this.selected && this.article && this.article.ModelNumber) {
      // const userStore = useUserStore()
      ctx.font = '50px Helvetica'
      const measure = ctx.measureText(this.article.ModelNumber)
      // let seasonData = userStore.activeCatalog?.CatalogCode === this.article.CatalogCode
      //   ? userStore.activeCatalog.Season
      //   : userStore.linkedCatalogDetails[this.article.CatalogCode].Season
      // const seasonDataMeasure = ctx.measureText(seasonData)
      ctx.fillStyle = '#1484f8'
      ctx.fillRect(-this.width! / 2, -this.height! / 2, measure.width, 60)
      // ctx.fillRect(this.width! / 2 - seasonDataMeasure.width, -this.height! / 2, seasonDataMeasure.width, 60)
      ctx.fillStyle = '#FFFFFF'
      ctx.fillText(this.article.ModelNumber, -this.width! / 2, -this.height! / 2 + 50)
      // ctx.fillText(seasonData, this.width! / 2 - seasonDataMeasure.width, -this.height! / 2 + 50)
    }

    if (this.icons.length > 0) {
      this.renderIcons(ctx)
    }
  }

  private renderIcons(ctx: CanvasRenderingContext2D) {
    if (this.icons.length === 0) { return }
    const iconsToShow = this.icons.slice(0, 3)
    const availableIcons = whiteboardConstants.availableIcons.map(icon => icon.name)
    const baseIconSize = 35
    const scaleFactor = Math.min(this.scaleX || 1, this.scaleY || 1)
    const iconSize = Math.max(16, baseIconSize * scaleFactor)
    const padding = 8 * scaleFactor
    const iconSpacing = 4 * scaleFactor
    const totalIconsWidth = (iconsToShow.length * iconSize) + ((iconsToShow.length - 1) * iconSpacing)
    const startX = (this.width || 0) - padding - totalIconsWidth
    const startY = (this.height || 0) - padding - iconSize
    ctx.save()
    ctx.translate(-this.width! / 2, -this.height! / 2)
    iconsToShow.forEach((iconName, index) => {
      if (!availableIcons.includes(iconName)) { return }
      const iconX = startX + (index * (iconSize + iconSpacing))
      const iconY = startY
      Utility.drawIcon(ctx, iconName, iconX, iconY, iconSize)
    })
    ctx.restore()
  }

  static getImageSrc(catalogCode: number, width: number, height: number, assets?: DuneAsset[], assetKey?: string) {
    let src = pngNoImage
    const userStore = useUserStore()
    const catalogDetails = userStore.linkedCatalogDetails[catalogCode] || userStore.activeCatalog
    if (userStore && catalogDetails && assets && assets.length > 0) {
      // in case of modelImage alway show the first image of the model article
      let asset = assets[0]
      // if requested for specific assetKey then search in _Assets, if does not exist in _Assets it should show placeholder
      if (utils.isValidStringValue(assetKey)) {
        const selectedAsset = assets.find(asset => asset.Id === assetKey || asset.Key === assetKey)
        if (selectedAsset) {
          asset = selectedAsset
        }
      }
      src = `${appConfig.AssetsUrl}/assets/content/${asset.StorageFile}?ContextKey=${encodeURIComponent(catalogDetails.ContextKey)}&f=webp&w=${width}&h=${height}&trim=true`
    }
    return src
  }
}

const f: any = fabric
f.WbModelImage = WbModelImage
